import React from 'react';
import PropTypes from 'prop-types';
import { ErrorText, VisuallyHidden, ImageCard, PillText } from '@web/atoms';
import {
  StyledFieldset,
  CardContainer,
  CardWrapper,
  PillContainer,
} from './styles.js';

export const ImageCardGroup = ({
  choices,
  value,
  name,
  legendText,
  onChange,
  onClick,
  disabled = false,
  isLoading = false,
  disabledPillText,
  direction = 'column',
  error,
  textDirection = 'column',
  cardTitleColor,
  cardDescriptionColor,
  checkedCardBackgroundColor,
  cardBorderColor,
  checkedCardBorderColor,
  recommendedValue,
  onlyShowRecommendedPill = true,
  cardGap = 4,
}) => {
  return (
    <StyledFieldset>
      <VisuallyHidden>
        <legend>{legendText}</legend>
      </VisuallyHidden>
      <CardContainer direction={direction} $cardGap={cardGap}>
        {choices.map(choice => {
          const checked = value === choice.value;
          const isChoiceDisabled = disabled || choice?.disabled;
          const showChoiceLoading = isLoading && checked;

          return (
            <CardWrapper key={choice.value}>
              <VisuallyHidden>
                <input
                  type="radio"
                  id={`${name}-${choice.value}`}
                  name={name}
                  value={choice.value}
                  checked={checked}
                  onChange={e => onChange(e.target.value)}
                  onClick={onClick}
                  disabled={isChoiceDisabled}
                />
              </VisuallyHidden>
              {choice?.disabled && disabledPillText ? (
                <PillContainer>
                  <PillText
                    text={disabledPillText}
                    variant="disabled"
                  ></PillText>
                </PillContainer>
              ) : (
                choice?.pillText &&
                (!onlyShowRecommendedPill ||
                  recommendedValue === choice?.value) && (
                  <PillContainer>
                    <PillText
                      text={choice?.pillText}
                      variant={choice?.pillVariant || 'success'}
                    ></PillText>
                  </PillContainer>
                )
              )}
              <ImageCard
                label={`Select ${choice.name}`}
                title={choice.name}
                description={choice.description}
                img={choice.image}
                checked={checked}
                onChange={() => onChange(choice.value)}
                disabled={isChoiceDisabled && !showChoiceLoading}
                isLoading={showChoiceLoading}
                unavailable={choice?.unavailable}
                unavailableText="This option is currently unavailable."
                textDirection={textDirection}
                titleColor={choice?.disabled ? 'text.tertiary' : cardTitleColor}
                descriptionColor={
                  choice?.disabled ? 'text.tertiary' : cardDescriptionColor
                }
                checkedBackgroundColor={checkedCardBackgroundColor}
                borderColor={cardBorderColor}
                checkedBorderColor={checkedCardBorderColor}
                totalCardCount={choices.length}
              />
            </CardWrapper>
          );
        })}
      </CardContainer>
      {error && <ErrorText>{error}</ErrorText>}
    </StyledFieldset>
  );
};

ImageCardGroup.displayName = 'ImageCardGroup';
ImageCardGroup.propTypes = {
  name: PropTypes.string.isRequired,
  choices: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      image: PropTypes.shape({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string.isRequired,
      }),
      disabled: PropTypes.bool,
      description: PropTypes.string.isRequired,
      pillText: PropTypes.string,
      pillVariant: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.string,
  recommendedValue: PropTypes.string,
  onlyShowRecommendedPill: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  direction: PropTypes.oneOf(['row', 'column']),
  error: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  disabledPillText: PropTypes.string,
  legendText: PropTypes.string.isRequired,
  textDirection: PropTypes.oneOf(['row', 'column']),
  cardTitleColor: PropTypes.string,
  cardDescriptionColor: PropTypes.string,
  checkedCardBackgroundColor: PropTypes.string,
  cardBorderColor: PropTypes.string,
  checkedCardBorderColor: PropTypes.string,
  cardGap: PropTypes.number,
};
